import { html } from "lit";
import { customElement, property, query, state } from "lit/decorators.js";
import { DimensionDefinition, IndicatorDefinition } from "@reporting/rp-types/definition";
import { msg } from "@omegagrid/localize";
import { ContextIndicator } from "@reporting/rp-types/analytics";
import { IndicatorAdapter } from "../../adapters/indicatorAdapter";
import { createRef, ref } from "lit/directives/ref.js";
import { TreeEvent, TreeMenu } from "@omegagrid/tree";
import { Button, ListItemRenderer, dom, constants as coreConstants, ListSortEvent, SortableList, Menu, Dropdown } from "@omegagrid/core";
import { BaseSection } from "./sectionBase";
import { getIndicatorKey } from "../../common";
import { IndicatorSettingsSection } from "./sectionIndicatorsSettings";
import { itemStyle, style } from "./sectionIndicators.style";
import constants from "../../constants";

@customElement('rp-fsection-indicators')
export class IndicatorSection extends BaseSection {

	static styles = [...BaseSection.styles, style];

	@property({type: Array})
	selectedIndicators: ContextIndicator[] = [];

	@property({type: Array})
	dimensionDefs: DimensionDefinition[] = [];

	@query('og-button.add')
	btnAdd: Button;

	@query('og-sortablelist')
	list: SortableList;

	@query('rp-fsection-indicators-settings', true)
	settings: IndicatorSettingsSection;

	@state()
	indicators: Map<string, IndicatorDefinition>;

	private _indicatorAdapter: IndicatorAdapter;
	get indicatorAdapter() {
		if (this._indicatorAdapter) return this._indicatorAdapter;
		this._indicatorAdapter = new IndicatorAdapter(this.module);
		return this._indicatorAdapter;
	}

	private treeMenuRef = createRef<TreeMenu>();
	get treeMenu() { return this.treeMenuRef.value }

	private itemMenuRef = createRef<Menu>();
	get itemMenu() { return this.itemMenuRef.value }

	async willUpdate(props: Map<PropertyKey, unknown>) {
		if (props.has('module') && this.module) {
			this._indicatorAdapter = null;
			this.indicators = await this.indicatorAdapter.getIndicators();
		}

		if (props.has('context') && this.context) {
			this.selectedIndicators = Array.isArray(this.context.indicators) ? this.context.indicators : [];
		}
	}

	updated() {
		this.list?.renderItems(true);
		this.layout();
	}

	disconnectedCallback() {
		this.itemMenu?.remove();
		this.treeMenu?.remove();
	}

	async openMenu() {
		dom.setSize(this.treeMenu, {w: this.btnAdd.offsetWidth});
		await this.treeMenu.open(this.btnAdd, 'right');
	}

	async openItemMenu(index: number, dropdown: Dropdown) {
		dom.showElement(this.settings);
		this.settings.indicator = this.selectedIndicators[index];
		dropdown.dropdown.appendChild(this.settings);
		await this.settings.updateComplete;
		dropdown.layout();
	}

	_onAddMenuSelect = (e: TreeEvent) => {
		const indicator = e.node.properties?.indicator as IndicatorDefinition;
		if (indicator) this.addIndicator(indicator);
	}

	_onSort = (e: ListSortEvent) => {
		const [item] = this.selectedIndicators.splice(e.fromIndex, 1);
		this.selectedIndicators.splice(e.toIndex, 0, item);
		this.list.renderItems(true);
	}

	getIndicatorByContextIndicator = (ind: ContextIndicator) => {
		return this.indicators.get(getIndicatorKey(ind));
	}

	indexCustomIndicators() {
		let i = 1;
		this.selectedIndicators.forEach((ind) => {
			if (ind.type == 'CUST') {
				ind.id = i++;
			}
		});
	}

	updateIndicators() {
		this.indexCustomIndicators();
		this.updateContext('indicators', this.selectedIndicators);
		this.list.size = this.selectedIndicators.length;
		this.list.requestUpdate();
		this.layout();
	}

	addIndicator(indicator: IndicatorDefinition) {
		const contextIdicator: Partial<ContextIndicator> = {
			id: indicator.id_indicator,
			type: indicator.type,
			balance: indicator.balance
		};

		if (indicator.type == 'CUST') {
			contextIdicator.id = null;
			contextIdicator.balance = false;
			contextIdicator.name = `Custom ${this.selectedIndicators.filter(ind => ind.type == 'CUST').length + 1}`;
		} else if (indicator.type == 'VAR') {
			contextIdicator.formula = indicator.formula;
		}

		this.selectedIndicators = [...this.selectedIndicators, contextIdicator as ContextIndicator];
		this.updateIndicators();
	}

	removeIndicator = (index: number) => {
		this.selectedIndicators.splice(index, 1);
		this.updateIndicators();
	}

	addFilter(index: number) {
		const indicator = this.selectedIndicators[index];
		indicator.filters = indicator.filters || [];
		indicator.filters.push({dim: null});
		this.updateIndicators();
	}

	itemRenderer: ListItemRenderer = (div: HTMLDivElement & {_init: boolean}, index: number) => {
		const indicator = this.getIndicatorByContextIndicator(this.selectedIndicators[index]);
		const contextIndicator = this.selectedIndicators[index];
		const name = contextIndicator?.name
			? (msg(contextIndicator.name) + (indicator?.description ? ` (${msg(indicator.description)})` : ''))
			: (indicator?.description ? msg(indicator.description) : msg('Unknown indicator'));

		return html`
			<div class="item-inner">
				<div class="item-indicator">
					<div style="flex: 0 ${coreConstants.LIST_ITEM_HEIGHT}px; margin-right: 4px">#${index + 1}</div>

					<div class="item-indicator-name" style="flex: 1">
						${contextIndicator.hidden ? html`<og-icon icon="eye-slash"></og-icon>` : ''}
						${contextIndicator.percentage > 0 ? html`<og-icon icon="percent"></og-icon>` : ''}
						${name}
						${contextIndicator.display_unit > 1 ? html`&nbsp;<span>(${msg(`abbr.${contextIndicator.display_unit}`)})</span>` : ''}
					</div>

					<og-button
						style="flex: 0 0 30px"
						color="tranparent"
						@click="${() => this.addFilter(index)}"
						@mousedown="${(e: Event) => e.stopPropagation()}">
						<div slot="content" class="btn-filter" style="display: flex; align-items: center; justify-content: center;">
							+<og-icon icon="filter" style="max-width: 10px"></og-icon>
						</div>
					</og-button>

					<og-dropdown
						noCaret
						icon="gear"
						color="tranparent"
						@dropdown.open="${(e: Event) => this.openItemMenu(index, e.target as Dropdown)}"
						@mousedown="${(e: Event) => e.stopPropagation()}">
					</og-dropdown>

					<og-button
						.icon="${{icon: 'trash', color: 'red'}}"
						color="tranparent"
						@click="${() => this.removeIndicator(index)}"
						@mousedown="${(e: Event) => e.stopPropagation()}">
					</og-button>
				</div>

				${['CUST', 'COMP'].includes(contextIndicator.type) ? html`
					<rp-fsection-indicators-formula
						.indicator="${contextIndicator}"
						@change="${() => this.updateIndicators()}"
						@mousedown="${(e: Event) => e.stopPropagation()}">
					</rp-fsection-indicators-formula>
				` : ''}

				${contextIndicator.filters?.length ? html`
					<rp-fsection-indicators-filters
						@change="${() => this.updateIndicators()}"
						@valuechange="${() => this.updateContext('indicators', this.selectedIndicators)}"
						@mousedown="${(e: Event) => e.stopPropagation()}"
						.indicator="${contextIndicator}"
						.context="${this.context}"
						.dimensions="${this.dimensionDefs}"
						.module="${this.module}">
					</rp-fsection-indicators-filters>
				` : ''}
			</div>
		`;
	}

	render = () => html`
		${this.selectedIndicators ? html`
			<og-sortablelist
				nativeHeight
				disabledSelect
				dynamicItemHeight
				@list.sort="${this._onSort}"
				.customStyle="${itemStyle}"
				.size="${this.selectedIndicators.length}"
				.itemRenderer="${this.itemRenderer}">
			</og-sortablelist>
		` : null}

		<div style="margin-top: ${constants.FILTER_PADDING / 2}px">
			<og-button
				class="add"
				color="transparent"
				@click="${() => this.openMenu()}"
				caret="down">
				<div slot="content">+ ${msg('Add indicator')}</div>
			</og-button>
		</div>

		<og-tree-menu ${ref(this.treeMenuRef)}
			style="display: none; z-index: 1000"
			maxHeight="200"
			@select="${this._onAddMenuSelect}"
			.adapter="${this.indicatorAdapter}">
		</og-tree-menu>

		<rp-fsection-indicators-settings
			@change="${() => this.updateIndicators()}"
			slot="inner-content"
			style="display: none">
		</rp-fsection-indicators-settings>
	`;

	layout = () => {
		const itemHeight = coreConstants.LIST_ITEM_HEIGHT;
		let h = 4;
		for (const ind of this.selectedIndicators) {
			h += itemHeight + constants.FILTER_PADDING;
			if (ind.type == 'CUST') h += itemHeight + constants.FILTER_PADDING;
			if (ind.filters?.length > 0) h += ind.filters.length * coreConstants.LIST_ITEM_HEIGHT;
		}
		dom.setSize(this.list, { h });
		this.list?.updateSliders();
	}

}
